import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheckCircle, FiX, FiClock, FiZap, FiSmartphone } = FiIcons;

const ComparisonSection = ({ className = "" }) => {
  const onlineAdvantages = [
    { text: "Skip the agent - buy directly online", icon: FiZap },
    { text: "No back-and-forth phone calls", icon: FiSmartphone },
    { text: "Instant policy documents", icon: FiCheckCircle },
    { text: "24/7 online access to your policy", icon: FiClock },
    { text: "Modify coverage anytime online", icon: FiCheckCircle },
    { text: "Claims filing through mobile app", icon: FiSmartphone }
  ];

  const traditionalProcess = [
    "Schedule appointment with agent",
    "Wait days/weeks for meeting", 
    "Multiple phone calls back and forth",
    "Wait for quotes and paperwork",
    "Sign documents in person",
    "Wait for policy to be processed"
  ];

  return (
    <section className={`py-20 bg-gradient-to-br from-gray-50 to-white ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
          >
            Traditional Insurance Agency vs. Online with Next Insurance
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            See why thousands choose the faster, easier way to get business insurance
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Online Advantages - Left Side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl border border-green-200 shadow-lg"
          >
            <div className="text-center mb-8">
              <div className="bg-green-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiZap} className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-green-800 mb-2">Online with Next Insurance</h3>
              <div className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                Total Time: 10 Minutes
              </div>
            </div>

            <div className="space-y-4">
              {onlineAdvantages.map((advantage, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-center space-x-3 bg-white p-4 rounded-lg shadow-sm"
                >
                  <div className="bg-green-100 p-2 rounded-full flex-shrink-0">
                    <SafeIcon icon={advantage.icon} className="h-5 w-5 text-green-600" />
                  </div>
                  <span className="text-gray-800 font-medium">{advantage.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Traditional Process - Right Side */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-red-50 to-orange-50 p-8 rounded-2xl border border-red-200 shadow-lg"
          >
            <div className="text-center mb-8">
              <div className="bg-red-500 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiClock} className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-red-800 mb-2">Traditional Insurance Agency</h3>
              <div className="bg-red-500 text-white px-4 py-2 rounded-full text-sm font-semibold">
                Total Time: 1-3 Weeks
              </div>
            </div>

            <div className="space-y-4">
              {traditionalProcess.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="flex items-center space-x-3 bg-white p-4 rounded-lg shadow-sm"
                >
                  <div className="bg-red-100 p-2 rounded-full flex-shrink-0">
                    <SafeIcon icon={FiX} className="h-5 w-5 text-red-600" />
                  </div>
                  <span className="text-gray-800">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Bottom Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center"
        >
          <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
            <div className="text-3xl font-bold text-primary-600 mb-2">10 Minutes</div>
            <div className="text-gray-600">Average Quote Time Online</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
            <div className="text-3xl font-bold text-secondary-600 mb-2">600,000+</div>
            <div className="text-gray-600">Businesses Choose Online</div>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
            <div className="text-3xl font-bold text-green-600 mb-2">95%</div>
            <div className="text-gray-600">Customer Satisfaction</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ComparisonSection;